import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Chapter } from './StoryHero';
import { inr } from './calculator/shared';
import { StepDates } from './calculator/StepDates';
import { StepGuests } from './calculator/StepGuests';
import { StepPackages, StepSpaces, StepRooms } from './calculator/StepBuild';
import { StepFood, StepAddons } from './calculator/StepExtras';
import { StepReview } from './calculator/StepReview';
import { StepDetails } from './calculator/StepDetails';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CostCalculator = ({ slug, venueName, onEnquire, bookedDates }) => {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [prefilled, setPrefilled] = useState(false);
  const [basics, setBasics] = useState({
    check_in_date: '', check_in_time: '14:00', check_out_date: '', check_out_time: '11:00',
    duration_id: '', guests: 100, overnight_guests: 0, children: 0, event_type: 'Wedding', functions_count: 3
  });
  const [selections, setSelections] = useState({ package_id: null, per_person_package_id: null, event_spaces: [], rooms: [], food: null, addons: [], promo_code: '' });
  const [result, setResult] = useState(null);
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '', city: '', budget_range: '', wedding_date: '', requirements: '' });
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [calcError, setCalcError] = useState(false);
  const debounceRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/pricing/${slug}/config`)
      .then(res => { if (res.data.enabled) setConfig(res.data); })
      .catch(() => {});
  }, [slug]);

  const steps = useMemo(() => {
    if (!config) return [];
    const s = [{ key: 'dates', label: 'Dates' }, { key: 'guests', label: 'Guests' }];
    if ((config.venue_packages || []).length || (config.per_person_packages || []).length) s.push({ key: 'packages', label: 'Packages' });
    if ((config.event_spaces || []).length) s.push({ key: 'spaces', label: 'Event Spaces' });
    if ((config.room_categories || []).length) s.push({ key: 'rooms', label: 'Rooms' });
    if (config.food?.availability !== 'no' && (config.food?.packages || []).length) s.push({ key: 'food', label: 'Food' });
    if ((config.addons || []).length) s.push({ key: 'addons', label: 'Add-Ons' });
    s.push({ key: 'review', label: 'Review' });
    s.push({ key: 'details', label: 'Your Details' });
    return s;
  }, [config]);

  const calcPayload = useCallback(() => ({
    check_in_date: basics.check_in_date,
    check_in_time: basics.check_in_time,
    check_out_date: basics.check_out_date,
    check_out_time: basics.check_out_time,
    guests: Number(basics.guests) || 0,
    overnight_guests: Number(basics.overnight_guests) || 0,
    children: Number(basics.children) || 0,
    event_type: basics.event_type,
    functions_count: Number(basics.functions_count) || 1,
    selections: {
      ...selections,
      event_spaces: (selections.event_spaces || []).map(s => ({ ...s, date: s.date || basics.check_in_date }))
    }
  }), [basics, selections]);

  const canCalculate = basics.check_in_date && basics.check_out_date;

  // If the guest already chose dates on the homepage/browse search, carry them in and skip step 1
  useEffect(() => {
    if (!config || prefilled) return;
    setPrefilled(true);
    const sp = new URLSearchParams(window.location.search);
    const ci = sp.get('check_in');
    const co = sp.get('check_out');
    const booked = new Set(bookedDates || []);
    if (ci && co && co > ci && !booked.has(ci)) {
      setBasics(prev => ({ ...prev, check_in_date: ci, check_out_date: co, duration_id: 'custom' }));
      setStep(1);
      toast.success("We carried your dates over — you're already a step ahead!");
    }
  }, [config, prefilled, bookedDates]);

  useEffect(() => {
    if (!config || !canCalculate) { setResult(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      axios.post(`${API_URL}/pricing/${slug}/calculate`, calcPayload())
        .then(res => { setResult(res.data); setCalcError(false); })
        .catch(() => setCalcError(true));
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [config, canCalculate, calcPayload, slug]);

  if (!config) return null;

  const current = steps[step];
  const goTo = (i) => {
    setStep(Math.max(0, Math.min(steps.length - 1, i)));
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const applyDates = (s) => {
    setBasics(prev => ({ ...prev, check_in_date: s.check_in, check_out_date: s.check_out, duration_id: 'custom' }));
    toast.success('Dates updated to an available window');
  };

  const submitEstimate = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/pricing/${slug}/estimate`, {
        calc: calcPayload(),
        customer: { ...customer, event_type: basics.event_type, guest_count: String(basics.guests) },
        accepted_rules: acceptedRules
      });
      setSubmitted(res.data);
      toast.success('Estimate created! Download your PDF below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create estimate. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="cost-calculator" className="bg-[#F3EFEB] border-y border-[#E5DCCF] py-16 md:py-24 font-wvc-body scroll-mt-4" data-testid="cost-calculator-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <div className="max-w-2xl">
          <Chapter n={4} label="Plan Your Investment" />
          <h2 className="font-wvc-display text-4xl sm:text-5xl font-light text-[#0A1710] tracking-tight leading-[1.05] mt-6">
            Build your celebration, see the real cost
          </h2>
          <p className="text-[#1A3626]/70 mt-4 text-sm font-light">
            Choose your dates, spaces, rooms and food &mdash; and watch the estimate update live. Download a detailed PDF when you&rsquo;re done. No calls needed until you&rsquo;re ready.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="flex gap-1.5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="calc-stepper">
              {steps.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => goTo(i)}
                  data-testid={`calc-step-btn-${s.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap border transition-colors ${i === step ? 'bg-[#1A3626] text-white border-[#1A3626]' : i < step ? 'bg-[#C6A87C]/20 text-[#1A3626] border-[#C6A87C]/40' : 'bg-white text-[#1A3626]/40 border-[#E5DCCF]'}`}
                >
                  <span>{i + 1}</span> {s.label}
                </button>
              ))}
            </div>

            <div className="bg-[#FDFCF8] border border-[#E5DCCF] p-5 sm:p-8">
              {current?.key === 'dates' && <StepDates config={config} basics={basics} setBasics={setBasics} bookedDates={bookedDates} onComplete={() => setTimeout(() => goTo(1), 900)} />}
              {current?.key === 'guests' && <StepGuests config={config} basics={basics} setBasics={setBasics} />}
              {current?.key === 'packages' && <StepPackages config={config} selections={selections} setSelections={setSelections} basics={basics} />}
              {current?.key === 'spaces' && <StepSpaces config={config} selections={selections} setSelections={setSelections} result={result} />}
              {current?.key === 'rooms' && <StepRooms config={config} selections={selections} setSelections={setSelections} basics={basics} />}
              {current?.key === 'food' && <StepFood config={config} selections={selections} setSelections={setSelections} />}
              {current?.key === 'addons' && <StepAddons config={config} selections={selections} setSelections={setSelections} basics={basics} />}
              {current?.key === 'review' && <StepReview result={result} selections={selections} setSelections={setSelections} onApplyDates={applyDates} />}
              {current?.key === 'details' && (
                <StepDetails
                  config={config} customer={customer} setCustomer={setCustomer}
                  acceptedRules={acceptedRules} setAcceptedRules={setAcceptedRules}
                  onSubmit={submitEstimate} isSubmitting={isSubmitting} submitted={submitted}
                  apiUrl={API_URL} onEnquire={() => onEnquire?.(`${basics.check_in_date} to ${basics.check_out_date}`, String(basics.guests))}
                />
              )}

              {!submitted && (
                <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#E5DCCF]">
                  <button
                    type="button"
                    onClick={() => goTo(step - 1)}
                    disabled={step === 0}
                    className="flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60 hover:text-[#1A3626] disabled:opacity-30 transition-colors"
                    data-testid="calc-back-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  {step < steps.length - 1 && (
                    <button
                      type="button"
                      onClick={() => goTo(step + 1)}
                      disabled={current?.key === 'dates' && !canCalculate}
                      className="flex items-center gap-1 bg-[#1A3626] text-white hover:bg-[#C6A87C] hover:text-[#0A1710] px-7 py-3 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-40"
                      data-testid="calc-next-btn"
                    >
                      Continue <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-4 bg-[#0A1710] text-white p-6" data-testid="calc-live-total">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C]">Live Estimated Total</p>
              <p className="font-wvc-display text-4xl font-light mt-2 tabular-nums" data-testid="calc-live-total-amount">
                {result ? inr(result.totals.grand_total) : '—'}
              </p>
              {result ? (
                <div className="mt-4 space-y-1.5 text-xs text-white/60">
                  {Object.entries(result.breakdown).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="capitalize">{k.replace('_', ' ')}</span>
                      <span className="tabular-nums">{inr(v)}</span>
                    </div>
                  ))}
                  {result.totals.discount_total > 0 && (
                    <div className="flex justify-between text-[#C6A87C]"><span>Discounts</span><span>- {inr(result.totals.discount_total)}</span></div>
                  )}
                  {result.totals.tax_total > 0 && (
                    <div className="flex justify-between"><span>Taxes</span><span className="tabular-nums">{inr(result.totals.tax_total)}</span></div>
                  )}
                  {result.totals.deposit_total > 0 && (
                    <div className="flex justify-between"><span>Refundable deposit</span><span className="tabular-nums">{inr(result.totals.deposit_total)}</span></div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-xs text-white/50 font-light">Pick your dates and guests to see a live estimate.</p>
              )}
              {result && !result.availability?.available && (
                <p className="mt-4 text-[11px] text-amber-300" data-testid="calc-live-unavailable">Selected dates unavailable — see Review for alternatives.</p>
              )}
              {calcError && (
                <p className="mt-4 text-[11px] text-amber-300" data-testid="calc-live-error">We couldn&rsquo;t update the estimate — please check your selections.</p>
              )}
              <p className="mt-5 text-[10px] text-white/40 font-light leading-relaxed">Estimated pricing — subject to final availability and venue confirmation.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
